import { api } from "@/lib/apiClient";

export interface OffpaperForm {
  id: number;
  offpaper_id: string;
  name: string | null;
  description?: string | null;
  created_at?: string | null;
  created_by_user?: string | null;
  last_updated_at?: string | null;
  file_access?: boolean | null;
  submission_count?: number | null;
}

export interface OffpaperSubmission {
  id: number;
  offpaper_id: string;
  form_offpaper_id: string;
  form_name?: string | null;
  label?: string | null;
  created_at?: string | null;
  created_by_user?: string | null;
  file_link?: string | null;
  file_id?: string | null;
  pdf_file_name?: string | null;
  values?: Record<string, unknown> | null;
}

export interface OffpaperFormDetail {
  form: OffpaperForm;
  field_keys: string[];
  placeholders: Record<string, string>;
  submissions: OffpaperSubmission[];
}

export interface OffpaperSyncAllResult {
  forms: number;
  inserted_submissions: number;
  per_form: Record<string, number>;
}

export function listOffpaperForms(): Promise<OffpaperForm[]> {
  return api.get<OffpaperForm[]>("/offpaper/forms");
}

export function getOffpaperFormDetail(formOffpaperId: string): Promise<OffpaperFormDetail> {
  return api.get<OffpaperFormDetail>(
    `/offpaper/forms/${encodeURIComponent(formOffpaperId)}/submissions`,
  );
}

/** Importiert Formulare + neue Submissionen aus der Offpaper-Cloud in die lokale DB. */
export function syncAllOffpaper(): Promise<OffpaperSyncAllResult> {
  return api.post<OffpaperSyncAllResult>("/offpaper/sync-all", undefined, {
    timeoutMs: 10 * 60_000,
  });
}

export function slugify(text: string | null | undefined): string {
  let value = String(text || "")
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return value || "x";
}

export function buildPlaceholder(formName: string, fieldName: string): string {
  if (!formName || !fieldName) return "";
  return `{{offpaper.${slugify(formName)}.${slugify(fieldName)}}}`;
}

export function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return "";

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (item === null || item === undefined) return "";
        if (typeof item === "object" && item !== null) {
          const obj = item as Record<string, unknown>;
          if ("label" in obj) return String(obj.label ?? "");
          if ("value" in obj) return String(obj.value ?? "");
          return JSON.stringify(item);
        }
        return String(item);
      })
      .filter(Boolean)
      .join(", ");
  }

  if (typeof value === "object" && value !== null) {
    const obj = value as Record<string, unknown>;
    if ("label" in obj) return String(obj.label ?? "");
    if ("value" in obj) return String(obj.value ?? "");
    return JSON.stringify(value);
  }

  return String(value);
}

/** Verschachtelte Offpaper-Werte in flache Key/Value-Paare (wie Legacy-UI). */
export function flattenOffpaperValues(
  values: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const flat: Record<string, unknown> = {};
  if (!values || typeof values !== "object") return flat;

  for (const [fieldName, fieldValue] of Object.entries(values)) {
    if (fieldValue && typeof fieldValue === "object" && !Array.isArray(fieldValue)) {
      const nested = fieldValue as Record<string, unknown>;
      const inner = Object.values(nested);

      if (
        inner.length > 0 &&
        inner.every((v) => v && typeof v === "object" && !Array.isArray(v) && "value" in v)
      ) {
        for (const [subName, subMeta] of Object.entries(nested)) {
          const colKey = `${fieldName} · ${subName}`;
          const meta = subMeta as Record<string, unknown> | null;
          flat[colKey] = meta && "value" in meta ? meta.value : subMeta;
        }
        continue;
      }

      if ("value" in nested || "label" in nested) {
        flat[fieldName] = nested.value ?? nested.label ?? "";
        continue;
      }
    }

    flat[fieldName] = fieldValue;
  }

  return flat;
}

export function collectFlatFieldKeys(submissions: OffpaperSubmission[]): string[] {
  const keys = new Set<string>();
  for (const sub of submissions) {
    for (const key of Object.keys(flattenOffpaperValues(sub.values || {}))) {
      keys.add(key);
    }
  }
  return Array.from(keys);
}

export interface OffpaperTableColumn {
  key: string;
  label: string;
  placeholder: string;
}

export function buildColumnsForFormDetail(data: OffpaperFormDetail): OffpaperTableColumn[] {
  const formName = data.form?.name || "";
  const fieldKeys = collectFlatFieldKeys(data.submissions || []);
  return [
    { key: "__idx", label: "#", placeholder: "" },
    { key: "__label", label: "Label", placeholder: buildPlaceholder(formName, "Label") },
    {
      key: "__created_at",
      label: "Erstellt am",
      placeholder: buildPlaceholder(formName, "Erstellt am"),
    },
    ...fieldKeys.map((k) => ({
      key: k,
      label: k,
      placeholder: buildPlaceholder(formName, k),
    })),
  ];
}

function escapeCsv(value: unknown): string {
  const s = String(value ?? "");
  if (/[;"\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function formatOffpaperDateTime(value?: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("de-DE");
}

export function exportFormDetailToCsv(data: OffpaperFormDetail): void {
  const submissions = data.submissions || [];
  if (!submissions.length) return;

  const columns = buildColumnsForFormDetail(data);
  const rows: string[] = [];

  rows.push(columns.map((col) => escapeCsv(col.placeholder || "")).join(";"));
  rows.push(columns.map((col) => escapeCsv(col.label || "")).join(";"));

  submissions.forEach((sub, idx) => {
    const flatValues = flattenOffpaperValues(sub.values || {});
    const row = columns.map((col) => {
      let raw: unknown;
      switch (col.key) {
        case "__idx":
          raw = idx + 1;
          break;
        case "__label":
          raw = sub.label || "";
          break;
        case "__created_at":
          raw = formatOffpaperDateTime(sub.created_at);
          break;
        default:
          raw = flatValues[col.key] != null ? flatValues[col.key] : "";
          break;
      }
      return escapeCsv(formatCellValue(raw));
    });
    rows.push(row.join(";"));
  });

  const blob = new Blob([rows.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const formName = data.form?.name || "offpaper";
  const filename = `${slugify(formName)}.csv`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
